import type { EinladungsAntwort, TeilnahmeStatus, TerminEinladung } from "../../services/graphMailService";
import { toGraphDate } from "../../utils/graphDatum";
import { graphAllDayDate } from "../calendar/allDay";

/**
 * T-229 — reine Ableitungen der Einladungsleiste im Lesefenster (ohne React,
 * testbar im node-Runner).
 */

/** Was die Leiste zum bisherigen Stand sagt; `null` = noch nicht geantwortet. */
export function statusText(status: TeilnahmeStatus): string | null {
    switch (status) {
        case "accepted":
            return "Sie haben zugesagt.";
        case "tentativelyAccepted":
            return "Sie haben mit Vorbehalt zugesagt.";
        case "declined":
            return "Sie haben abgesagt.";
        case "organizer":
            return "Sie organisieren diesen Termin.";
        default:
            return null;
    }
}

/** Welche Antwort entspricht dem bisherigen Stand — dieser Knopf ist gedrückt. */
export function aktiveAntwort(status: TeilnahmeStatus): EinladungsAntwort | null {
    if (status === "accepted") return "zusagen";
    if (status === "tentativelyAccepted") return "vorbehalt";
    if (status === "declined") return "absagen";
    return null;
}

/** Ob die Leiste Knöpfe zeigt: nicht beim eigenen und nicht beim abgesagten Termin. */
export function beantwortbar(e: Pick<TerminEinladung, "abgesagt" | "istOrganisator">): boolean {
    return !e.abgesagt && !e.istOrganisator;
}

/**
 * „Do., 09.10.2026, 10:00–11:00" bzw. „Do., 09.10.2026 (ganztägig)".
 * Graph liefert `dateTime` ohne Zone (hier immer UTC angefragt) — `toGraphDate`
 * hängt das Z an, sonst stünde der Termin um den Zonen-Versatz verschoben da.
 */
export function terminZeitText(e: Pick<TerminEinladung, "start" | "ende" | "ganztaegig">): string {
    if (!e.start) return "";
    const tag = (d: Date) =>
        d.toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit", year: "numeric" });
    const uhr = (d: Date) => d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
    if (e.ganztaegig) return `${tag(graphAllDayDate(e.start.dateTime))} (ganztägig)`;
    const start = toGraphDate(e.start.dateTime);
    if (Number.isNaN(start.getTime())) return "";
    const ende = e.ende ? toGraphDate(e.ende.dateTime) : null;
    if (!ende || Number.isNaN(ende.getTime())) return `${tag(start)}, ${uhr(start)}`;
    if (tag(start) === tag(ende)) return `${tag(start)}, ${uhr(start)}–${uhr(ende)}`;
    return `${tag(start)}, ${uhr(start)} – ${tag(ende)}, ${uhr(ende)}`;
}

export const ANTWORT_LABEL: Record<EinladungsAntwort, string> = {
    zusagen: "Zusagen",
    vorbehalt: "Mit Vorbehalt",
    absagen: "Absagen",
};

/**
 * T-271 (28.10.2026): Muss ein Klick überhaupt an den Server? Dieselbe Antwort
 * ohne Kommentar ändert nichts — die Leiste zeigt den Stand schon.
 */
export function mussSenden(antwort: EinladungsAntwort, status: TeilnahmeStatus, kommentar: string): boolean {
    return aktiveAntwort(status) !== antwort || kommentar.trim() !== "";
}

const ALLGEMEINER_FEHLER = "Die Antwort konnte nicht gesendet werden. Bitte erneut versuchen oder in Outlook antworten.";

/**
 * T-271: Fehlertext nach HTTP-Status. `fetchJson` baut die Meldung als
 * „403 Forbidden: {…}". Die Meldung von Graph steht im Server-Log
 * (`translateError`) und in der Konsole, nicht in der Leiste.
 */
export function einladungsFehlerText(err: unknown): string {
    const text = err instanceof Error ? err.message : typeof err === "string" ? err : "";
    const status = Number(text.match(/^\s*([1-5]\d{2})\b/)?.[1]);
    if (status === 403) {
        return "Keine Berechtigung, in diesem Postfach auf Einladungen zu antworten. Bitte in Outlook antworten.";
    }
    if (status === 404) return "Den Termin gibt es nicht mehr — er wurde abgesagt oder aus dem Kalender gelöscht.";
    if (status === 400) return "Outlook hat die Antwort abgelehnt. Bitte in Outlook antworten.";
    return ALLGEMEINER_FEHLER;
}
